export type UnitBaseColor = 'sky' | 'lime' | 'orange' | 'purple' | 'pink';

const baseColors: UnitBaseColor[] = ['sky', 'lime', 'orange', 'purple', 'pink'];

// Maps a two-digit unit number (received as a string, e.g. "01", "06", "10") to one of the base
// colors, cycling back to the start once the palette is exhausted ("06" -> same color as "01").
// Units are 1-indexed: "00" (and any non-positive or unparseable value) has no dedicated slot, so
// it falls back to the default `sky`, matching resolveUnitBaseColor's no-unit fallback.
export const getColorByUnit = (name: string): UnitBaseColor => {
  const unitNumber = parseInt(name, 10);
  if (Number.isNaN(unitNumber) || unitNumber < 1) return 'sky';
  return baseColors[(unitNumber - 1) % baseColors.length];
};

// Resolves the base color for an optional unit name (undefined when no unit is selected), defaulting
// to `sky` to match all unit-colored dashboard components.
export const resolveUnitBaseColor = (name: string | undefined): UnitBaseColor => (name ? getColorByUnit(name) : 'sky');

// ---- Per-component color palettes, keyed by base color -----------------------------------------
// Tailwind classes below are spelled out statically (never built via template literals) so the
// JIT compiler can detect and keep them.

// Unit-header skill badges (UnitSkillBadge → DashboardUnitHeaderIcons/*). The full badge is an SVG;
// only the "chrome" (ring, highlight, bubble outline, count number) plus the Foundations arch/books
// take the unit color — the inner support/target icons keep their own multicolor fills. Classes are
// applied as `tw-fill-*` / `tw-stroke-*` on the SVG paths (`tw-text-*` for the overlaid count text).
// Spelled out statically so the JIT keeps them. The `pink` row mirrors the original hardcoded hex.
export interface UnitHeaderBadgeColors {
  // Circle badges (support / target).
  ring: string; // outer disc (#F26599)
  highlight: string; // top-left shine (#F490B0)
  bubbleBorder: string; // count-bubble outline (#F7B8CA)
  count: string; // overlaid count text (#B72866)
  // Foundations arch.
  archBackground: string; // arch fill (#FDEDF1)
  archShadow: string; // inner drop-shadow ellipse (#FBDAE3)
  archBorder: string; // arch stroke (#F490B0)
  bookFace: string; // lighter book faces (and the sparkle, unless `sparkle` overrides it)
  bookSpine: string; // darker book spines
  sparkle?: string; // sparkle above the books; defaults to `bookFace`
}

export const unitHeaderBadgeColors: Record<UnitBaseColor, UnitHeaderBadgeColors> = {
  sky: {
    ring: 'tw-fill-sky-400',
    highlight: 'tw-fill-sky-300',
    bubbleBorder: 'tw-fill-sky-200',
    count: 'tw-text-sky-700',
    archBackground: 'tw-fill-sky-100',
    archShadow: 'tw-fill-sky-200',
    archBorder: 'tw-stroke-sky-300',
    bookFace: 'tw-fill-sky-300',
    bookSpine: 'tw-fill-sky-500',
  },
  lime: {
    ring: 'tw-fill-lime-400',
    highlight: 'tw-fill-lime-300',
    bubbleBorder: 'tw-fill-lime-200',
    count: 'tw-text-lime-700',
    archBackground: 'tw-fill-lime-100',
    archShadow: 'tw-fill-lime-200',
    archBorder: 'tw-stroke-lime-300',
    bookFace: 'tw-fill-lime-300',
    bookSpine: 'tw-fill-lime-500',
  },
  orange: {
    ring: 'tw-fill-orange-400',
    highlight: 'tw-fill-orange-300',
    bubbleBorder: 'tw-fill-orange-200',
    count: 'tw-text-orange-700',
    archBackground: 'tw-fill-orange-100',
    archShadow: 'tw-fill-orange-200',
    archBorder: 'tw-stroke-orange-300',
    bookFace: 'tw-fill-orange-300',
    bookSpine: 'tw-fill-orange-500',
  },
  purple: {
    ring: 'tw-fill-purple-400',
    highlight: 'tw-fill-purple-300',
    bubbleBorder: 'tw-fill-purple-200',
    count: 'tw-text-purple-700',
    archBackground: 'tw-fill-purple-100',
    archShadow: 'tw-fill-purple-200',
    archBorder: 'tw-stroke-purple-300',
    bookFace: 'tw-fill-purple-300',
    bookSpine: 'tw-fill-purple-500',
  },
  pink: {
    ring: 'tw-fill-pink-400',
    highlight: 'tw-fill-pink-300',
    bubbleBorder: 'tw-fill-pink-200',
    count: 'tw-text-pink-700',
    archBackground: 'tw-fill-pink-100',
    archShadow: 'tw-fill-pink-200',
    archBorder: 'tw-stroke-pink-300',
    bookFace: 'tw-fill-pink-300',
    bookSpine: 'tw-fill-pink-500',
  },
};

export const lockedHeaderBadgeColors: UnitHeaderBadgeColors = {
  ring: 'tw-fill-stone-300',
  highlight: 'tw-fill-stone-200',
  bubbleBorder: 'tw-fill-stone-100',
  count: 'tw-text-stone-600',
  archBackground: 'tw-fill-stone-50',
  archShadow: 'tw-fill-stone-100',
  archBorder: 'tw-stroke-stone-300',
  bookFace: 'tw-fill-stone-300',
  bookSpine: 'tw-fill-stone-600',
  sparkle: 'tw-fill-amber-200',
};

export interface UnitFoundationsCardColors {
  background: string;
  svgFill: string;
  svgStroke: string;
}

// Foundations card (DashboardCards). `pink` mirrors the hardcoded background #F26599 / stroke #F490B0.
export const unitFoundationsCardColors: Record<UnitBaseColor, UnitFoundationsCardColors> = {
  sky: { background: '!tw-bg-sky-400', svgFill: 'tw-fill-sky-500', svgStroke: 'tw-stroke-sky-300' },
  lime: { background: '!tw-bg-lime-400', svgFill: 'tw-fill-lime-500', svgStroke: 'tw-stroke-lime-300' },
  orange: { background: '!tw-bg-orange-400', svgFill: 'tw-fill-orange-500', svgStroke: 'tw-stroke-orange-300' },
  purple: { background: '!tw-bg-purple-400', svgFill: 'tw-fill-purple-500', svgStroke: 'tw-stroke-purple-300' },
  pink: { background: '!tw-bg-pink-400', svgFill: 'tw-fill-pink-500', svgStroke: 'tw-stroke-pink-300' },
};

export interface UnitConnectorColors {
  // Lightest shade — used for the dashed "passed" overlay (was #F7B8CA / pink-200).
  fillLight: string;
  // Main track shade (was #F490B0 / pink-300).
  fillBase: string;
  // Darkest accent — used for end-node circles and path badges (was #E33480).
  fillAccent: string;
  strokeAccent: string;
  // Lighter border shade — a softer edge over the accent-filled path badge (a bit lighter than the fill).
  strokeBase: string;
}

// Skill-path connectors (DashboardConnectors).
export const unitConnectorColors: Record<UnitBaseColor, UnitConnectorColors> = {
  sky: {
    fillLight: 'tw-fill-sky-200',
    fillBase: 'tw-fill-sky-300',
    fillAccent: 'tw-fill-sky-500',
    strokeAccent: 'tw-stroke-sky-500',
    strokeBase: 'tw-stroke-sky-300',
  },
  lime: {
    fillLight: 'tw-fill-lime-200',
    fillBase: 'tw-fill-lime-300',
    fillAccent: 'tw-fill-lime-500',
    strokeAccent: 'tw-stroke-lime-500',
    strokeBase: 'tw-stroke-lime-300',
  },
  orange: {
    fillLight: 'tw-fill-orange-200',
    fillBase: 'tw-fill-orange-300',
    fillAccent: 'tw-fill-orange-500',
    strokeAccent: 'tw-stroke-orange-500',
    strokeBase: 'tw-stroke-orange-300',
  },
  purple: {
    fillLight: 'tw-fill-purple-200',
    fillBase: 'tw-fill-purple-300',
    fillAccent: 'tw-fill-purple-500',
    strokeAccent: 'tw-stroke-purple-500',
    strokeBase: 'tw-stroke-purple-300',
  },
  pink: {
    fillLight: 'tw-fill-pink-200',
    fillBase: 'tw-fill-pink-300',
    fillAccent: 'tw-fill-pink-500',
    strokeAccent: 'tw-stroke-pink-500',
    strokeBase: 'tw-stroke-pink-300',
  },
};

export interface UnitCardHighlightColors {
  border: string;
  background: string;
}

// Inferred/Passed skill card highlight (DashboardCard). Uses inline-style hex values (not Tailwind
// classes) because the highlight border must override the SCSS `.target-card` border. `pink`
// mirrors the previously hardcoded border #F490B0 / background #FBDAE3.
export const unitCardHighlightColors: Record<UnitBaseColor, UnitCardHighlightColors> = {
  sky: { border: '#7dd3fc', background: '#e0f2fe' },
  lime: { border: '#bef264', background: '#ecfccb' },
  orange: { border: '#fdba74', background: '#ffedd5' },
  purple: { border: '#d8b4fe', background: '#f3e8ff' },
  pink: { border: '#F490B0', background: '#FBDAE3' },
};

// Completed target-card highlight (DashboardCard). Kept as inline-style hex (like unitCardHighlightColors
// above) to override the SCSS `.target-card` border: a darker border and a pale-yellow background over
// the base `#ffeaa7`. No exact Tailwind token exists for these two shades.
export const targetCardHighlightColors: UnitCardHighlightColors = { border: '#ffdd6d', background: '#fff8e1' };

// Darkest unit text tone for the skill-card title on Passed/Inferred cards (DashboardCard) — a dark
// shade of the selected unit's base color, readable over the light highlight background. Tailwind
// classes spelled out statically so the JIT keeps them.
export const unitDarkestTextColor: Record<UnitBaseColor, string> = {
  sky: 'tw-text-sky-700',
  lime: 'tw-text-lime-700',
  orange: 'tw-text-orange-700',
  purple: 'tw-text-purple-700',
  pink: 'tw-text-pink-700',
};

// Unit accent as a TEXT color (the `-500` shade — the same tone the path connectors/badges fill with,
// `unitConnectorColors.fillAccent`). Used by UnitPathSegmentLabel to tint the segment the student is
// currently working on. Classes spelled out statically so the JIT keeps them.
export const unitAccentTextColor: Record<UnitBaseColor, string> = {
  sky: 'tw-text-sky-500',
  lime: 'tw-text-lime-500',
  orange: 'tw-text-orange-500',
  purple: 'tw-text-purple-500',
  pink: 'tw-text-pink-500',
};

// Gray outline shared by every blocked card, matching the default `.p-card` border — the Tailwind
// `neutral-100` token (#F1F0EE).
export const blockedCardBorderColor = '#F1F0EE';

// Solid unit accent (Tailwind `-500` hex) for filled UI such as the unit-header segment progress bar's
// value. It is the same accent shade the path connectors/badges use (`unitConnectorColors.fillAccent`
// = `tw-fill-*-500`). Kept as hex (not a Tailwind class) because it is applied as an inline background
// on the PrimeReact progress-bar value element via the `pt` passthrough.
export const unitProgressBarColor: Record<UnitBaseColor, string> = {
  sky: '#0ea5e9',
  lime: '#84cc16',
  orange: '#f97316',
  purple: '#a855f7',
  pink: '#ec4899',
};
